import { Form, Head, Link } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { CompanySwitcher } from "@/components/company-switcher";
import { LanguageChoice } from "@/components/language-choice";
import {
  FormContent,
  FormSection,
  PageCanvas,
  PageHeader,
} from "@/components/page-canvas";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { onboardingActivationPath, userBillingPortalPath } from "@/routes";
import type { OnboardingProps } from "@/components/onboarding/onboarding-form";

export default function Activation({
  setup,
  access,
  checkout_state,
  stripe_configured,
  trial,
}: {
  setup: { account_name: string; location_name: string | null };
  access: { trial_eligible: boolean; recoverable: boolean };
  checkout_state: "ready" | "failed" | "cancelled" | "incomplete";
  stripe_configured: boolean;
  trial: OnboardingProps["trial"];
}) {
  const { t, i18n } = useTranslation();
  const terms = {
    days: trial.days,
    plan: t(`billing_ui.plans.${trial.plan_key}.name`),
    post_plan: t(`billing_ui.plans.${trial.price.plan_key}.name`),
    amount: new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: trial.currency.toUpperCase(),
      currencyDisplay: "code",
    }).format(trial.price.amount_cents / 100),
    interval: t(`billing_ui.${trial.price.interval}`),
  };
  return (
    <PageCanvas as="main" className="min-h-svh">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <CompanySwitcher />
        <LanguageChoice />
      </div>
      <Head title={t("activation.title")} />
      <FormContent align="center" className="space-y-6">
        <PageHeader
          title={t("activation.title")}
          description={t("activation.description")}
        />
        <FormSection title={t("activation.saved_title")}>
          <dl className="space-y-3">
            <div>
              <dt className="text-muted-foreground text-sm">
                {t("access.business")}
              </dt>
              <dd className="break-words font-medium">{setup.account_name}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">
                {t("access.location")}
              </dt>
              <dd className="break-words font-medium">{setup.location_name}</dd>
            </div>
          </dl>
        </FormSection>
        <p
          role={
            checkout_state === "ready" && stripe_configured ? "status" : "alert"
          }
          className="rounded-md border p-4 text-sm"
        >
          {t(
            checkout_state !== "ready"
              ? `activation.${checkout_state}`
              : !stripe_configured
                ? "activation.unavailable"
                : "activation.ready",
          )}
        </p>
        {!stripe_configured && checkout_state !== "ready" && (
          <p role="status" className="text-muted-foreground text-sm">
            {t("activation.unavailable")}
          </p>
        )}
        <FormSection
          title={t(
            access.trial_eligible
              ? "activation.trial_title"
              : "activation.subscription_title",
          )}
        >
          <p className="text-muted-foreground text-sm">
            {access.trial_eligible
              ? t("onboarding.trial_notice", terms)
              : t("activation.paid_terms", terms)}
          </p>
          <Form
            action={
              access.recoverable
                ? userBillingPortalPath()
                : onboardingActivationPath()
            }
            method="post"
          >
            {({ processing }) => (
              <Button type="submit" disabled={processing || !stripe_configured}>
                {processing && <Spinner />}
                {t(
                  access.recoverable
                    ? "activation.recover"
                    : "onboarding.checkout",
                )}
              </Button>
            )}
          </Form>
        </FormSection>
        <Link
          href="/logout"
          method="delete"
          as="button"
          className="min-h-11 text-sm underline underline-offset-4"
        >
          {t("recovery.logout")}
        </Link>
      </FormContent>
    </PageCanvas>
  );
}
